'use client'

import { motion } from 'framer-motion'
import { Clock, MapPin, Utensils, Heart, Car } from 'lucide-react'
import Image from "next/image"
import dynamic from 'next/dynamic'

const MapMariage = dynamic(() => import('../Programme/map'), { ssr: false })

interface Event {
	time: string;
	title: string;
	description: string;
	icon: any;
	location: string;
	image: string;
}

export default function Programme() {
	const events: Event[] = [
		{
			time: '13h45',
			title: 'Cérémonie Religieuse',
			description: "Collegiale Notre-Dame de l'Assomption",
			icon: Heart,
			location: '2 place du cloitre, 27440 Ecouis',
			image: '/img/citadelle.jpeg'
		},
		{
			time: '16h30',
			title: 'Accueil au Manoir de Corny',
			description: 'Manoir de Corny',
			icon: Heart,
			location: '3 Chemin de la Vieille Coté, 27700 Fresnelle-en-Vexin',
			image: '/img/manoir.png'
		},
		{
			time: '17h00',
			title: 'Cocktail de mariage',
			description: 'Apéritif et photos / Salle de récéption',
			icon: Utensils,
			location: 'La dînière',
			image: '/img/dimiere2.jpg'
		},
		{
			time: '20h00',
			title: 'Dîner',
			description: 'Repas et discours',
			icon: Utensils,
			location: 'La dînière',
			image: '/img/discours.jpg'
		},
	]

	return (
		<section id="programme" className="py-10 px-10 section-bg-dark">
			<div className="container mx-auto px-2 sm:px-4">
				{/* En-tête */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8 }}
					viewport={{ once: true }}
					className="text-center mb-8"
				>
					<h2 id="programme2" className="heading-secondary mb-4">Programme de la journée</h2>
					<div className="w-24 h-1 bg-bordeaux-600 mx-auto mb-4"></div>
				</motion.div>

				{/* Timeline responsive */}
				<div className="max-w-7xl mx-auto py-8 grid gap-y-8 relative
		before:content-[''] before:bg-bordeaux-200 before:w-0.5 before:absolute before:top-0 before:bottom-0 before:left-1/2 before:-translate-x-1/2 before:z-0
		sm:gap-y-4"
				>
					{events.map((event, idx) => {
						const isLeft = idx % 2 === 0
						return (
							<motion.div
								key={idx}
								initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
								whileInView={{ opacity: 1, x: 0 }}
								transition={{ duration: 0.6, delay: idx * 0.1 }}
								viewport={{ once: true }}
								className="
									flex flex-col
									sm:grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center
									w-full
									z-10
									"
							>
								{/* Colonne gauche */}
								<div className="hidden sm:flex justify-end pr-4 w-full">
									{isLeft && (
										<div className="card w-full max-w-4xl xl:max-w-5xl 2xl:max-w-6xl p-4 sm:p-6 md:p-8 lg:p-10 rounded-2xl shadow-2xl bg-white overflow-hidden">
											<div className="flex flex-col sm:flex-row items-start sm:space-x-6">
												{/* Contenu à gauche */}
												<div className="flex-1 order-2 sm:order-1">
													<div className="flex items-center space-x-2 mb-2">
														<Clock className="w-5 h-5 md:w-6 md:h-6 text-gold-600" />
														<span className="text-lg md:text-xl font-semibold text-bordeaux-800">{event.time}</span>
													</div>
													<h3 className="text-xl md:text-2xl lg:text-3xl text-bordeaux-800 mb-2">{event.title}</h3>
													<p className="text-bordeaux-700 leading-relaxed mb-3 text-base md:text-lg">{event.description}</p>
													<div className="flex items-center space-x-2 text-[10px] md:text-base text-bordeaux-600">
														<MapPin className="w-4 h-4 md:w-5 md:h-5" />
														<span>{event.location}</span>
													</div>
												</div>
												{/* Image à droite */}
												<div className="relative w-full sm:w-48 h-32 sm:h-36 rounded-xl overflow-hidden mb-4 sm:mb-0 flex-shrink-0 order-1 sm:order-2">
													<Image
														src={event.image}
														alt={event.title}
														fill
														className="object-cover hover:scale-105 transition-transform duration-300"
														sizes="(max-width: 768px) 100vw, 200px"
													/>
													<div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
												</div>
											</div>
										</div>
									)}
								</div>
								{/* Dot */}
								<div className="flex flex-col items-center my-4 sm:my-0">
									<span className="w-4 h-4 bg-bordeaux-600 rounded-full border-4 border-white shadow-lg"></span>
								</div>
								{/* Colonne droite */}
								<div className="hidden sm:flex justify-start pl-4 w-full">
									{!isLeft && (
										<div className="card w-full max-w-4xl xl:max-w-5xl 2xl:max-w-6xl p-4 sm:p-6 md:p-8 lg:p-10 rounded-2xl shadow-2xl bg-white overflow-hidden">
											<div className="flex flex-col sm:flex-row items-start sm:space-x-6">
												{/* Contenu à gauche */}
												<div className="flex-1 order-2 sm:order-1">
													<div className="flex items-center space-x-2 mb-2">
														<Clock className="w-5 h-5 md:w-6 md:h-6 text-gold-600" />
														<span className="text-lg md:text-xl font-semibold text-bordeaux-800">{event.time}</span>
													</div>
													<h3 className="text-xl md:text-xl lg:text-2xl text-bordeaux-800 mb-2">{event.title}</h3>
													<p className="text-bordeaux-700 leading-relaxed mb-3 text-base md:text-lg">{event.description}</p>
													<div className="flex items-center space-x-2 text-sm text-bordeaux-600">
														<MapPin className="w-4 h-4 md:w-5 md:h-5" />
														<span>{event.location}</span>
													</div>
												</div>
												{/* Image à droite */}
												<div className="relative w-full sm:w-48 h-32 sm:h-36 rounded-xl overflow-hidden mb-4 sm:mb-0 flex-shrink-0 order-1 sm:order-2">
													<Image
														src={event.image}
														alt={event.title}
														fill
														className="object-cover hover:scale-105 transition-transform duration-300"
														sizes="(max-width: 768px) 100vw, 200px"
													/>
													<div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
												</div>
											</div>
										</div>
									)}
								</div>
								{/* Mobile : event sous le dot */}
								<div className="flex sm:hidden w-full mt-4">
									<div className="card w-full max-w-full p-4 rounded-2xl shadow-2xl bg-white overflow-hidden">
										<div className="flex flex-col items-start">
											{/* Image en haut sur mobile */}
											<div className="relative w-full h-40 rounded-xl overflow-hidden mb-4">
												<Image
													src={event.image}
													alt={event.title}
													fill
													className="object-cover hover:scale-105 transition-transform duration-300"
													sizes="100vw"
												/>
												<div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
												{/* Heure en overlay sur mobile */}
												<div className="absolute top-3 left-3 bg-bordeaux-600/90 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
													{event.time}
												</div>
											</div>
											{/* Contenu */}
											<div className="w-full">
												<h3 className="text-xl text-bordeaux-800 mb-2">{event.title}</h3>
												<p className="text-bordeaux-700 leading-relaxed mb-3">{event.description}</p>
												<div className="flex items-center space-x-2 text-sm text-bordeaux-600">
													<MapPin className="w-4 h-4" />
													<span>{event.location}</span>
												</div>
											</div>
										</div>
									</div>
								</div>
							</motion.div>
						)
					})}
				</div>

				{/* Reste du code inchangé... */}
				<hr className="my-16 border-bordeaux-200" />

				<div className="text-center mt-10 mb-8">
					<h2 className="heading-secondary mb-2">Informations pratiques</h2>
					<div className="w-24 h-1 bg-bordeaux-600 mx-auto mb-4"></div>
					<p className="text-lg text-bordeaux-700 max-w-2xl mx-auto">
						Retrouvez ici l'adresse de l'église, du domaine ainsi que des suggestions de logements.
					</p>
				</div>

				{/* Section hôtels - code inchangé */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.8 }}
					viewport={{ once: true }}
					className="mt-20"
				>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start mb-8">
						{/* Colonne gauche : la carte */}
						<div className="h-full flex flex-col">
							<MapMariage />
						</div>

						{/* Colonne droite : hôtels en grille 2x3 */}
						<div className="flex flex-col gap-6 h-full">
							{/* Grille d'hôtels 2 par 2 */}
							<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
								{/* Ligne 1 - Hôtels 1 & 2 */}
								<a href="https://www.booking.com/hotel/fr/chic-et-charme-cottage.fr.html" target="_blank" rel="noopener">
									<motion.div
										initial={{ opacity: 0, y: 20 }}
										whileInView={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.7, delay: 0.1 }}
										viewport={{ once: true }}
										className="group bg-gradient-to-br from-white to-bordeaux-50/30 rounded-2xl shadow-xl border border-bordeaux-100/50 overflow-hidden hover:shadow-2xl hover:border-gold-300 transition-all duration-300 transform hover:-translate-y-1 h-full"
									>
										<div className="relative w-full h-40 overflow-hidden">
											<Image
												src="/img/charme1.jpg"
												alt="Hôtel Chic & Charme"
												fill
												className="object-cover transition-transform duration-500 group-hover:scale-110 p-1 rounded-2xl"
												sizes="(max-width: 768px) 100vw, 300px"
											/>
											<div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
										</div>
										<div className="p-5">
											<h4 className="text-lg font-bold text-bordeaux-800 mb-3 flex items-center gap-2 group-hover:text-bordeaux-900 transition-colors">
												<div className="p-1 bg-gold-100 rounded-full">
													<MapPin className="w-4 h-4 text-gold-600" />
												</div>
												Hôtel Chic & Charme
											</h4>
											<div className="space-y-2 mb-4">
												<p className="text-sm text-bordeaux-600 flex items-center gap-2">
													<div className="p-1 bg-bordeaux-100 rounded-full">
														<Car className="w-3 h-3 text-bordeaux-500" />
													</div>
													<span className="font-medium">20 min du domaine</span>
												</p>
												<p className="text-xs text-bordeaux-500 leading-relaxed pl-7">
													25/27 rue Grande, Le Petit Andelys, 27700 Les Andelys
												</p>
											</div>
										</div>
									</motion.div>
								</a>

								<a href="https://www.booking.com/hotel/fr/la-maison-blanche-ecouis.fr.html" target="_blank" rel="noopener">
									<motion.div
										initial={{ opacity: 0, y: 20 }}
										whileInView={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.7, delay: 0.2 }}
										viewport={{ once: true }}
										className="group bg-gradient-to-br from-white to-bordeaux-50/30 rounded-2xl shadow-xl border border-bordeaux-100/50 overflow-hidden hover:shadow-2xl hover:border-gold-300 transition-all duration-300 transform hover:-translate-y-1 h-full"
									>
										<div className="relative w-full h-40 overflow-hidden">
											<Image
												src="/img/fief3.jpg"
												alt="Cottage Le Fief"
												fill
												className="object-cover transition-transform duration-500 group-hover:scale-110 p-1 rounded-2xl"
												sizes="(max-width: 768px) 100vw, 300px"
											/>
											<div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
										</div>
										<div className="p-5">
											<h4 className="text-lg font-bold text-bordeaux-800 mb-3 flex items-center gap-2 group-hover:text-bordeaux-900 transition-colors">
												<div className="p-1 bg-gold-100 rounded-full">
													<MapPin className="w-4 h-4 text-gold-600" />
												</div>
												Cottage Le Fief
											</h4>
											<div className="space-y-2 mb-4">
												<p className="text-sm text-bordeaux-600 flex items-center gap-2">
													<div className="p-1 bg-bordeaux-100 rounded-full">
														<Car className="w-3 h-3 text-bordeaux-500" />
													</div>
													<span className="font-medium">20 min du domaine</span>
												</p>
												<p className="text-xs text-bordeaux-500 leading-relaxed pl-7">
													4 Place de l'Église, 27420 Suzay
												</p>
											</div>
										</div>
									</motion.div>
								</a>

								{/* Ligne 2 - Hôtels 3 & 4 */}
								<a href="https://www.booking.com/hotel/fr/chateau-de-bonnemare.fr.html" target="_blank" rel="noopener">
									<motion.div
										initial={{ opacity: 0, y: 20 }}
										whileInView={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.7, delay: 0.3 }}
										viewport={{ once: true }}
										className="group bg-gradient-to-br from-white to-bordeaux-50/30 rounded-2xl shadow-xl border border-bordeaux-100/50 overflow-hidden hover:shadow-2xl hover:border-gold-300 transition-all duration-300 transform hover:-translate-y-1 h-full"
									>
										<div className="relative w-full h-40 overflow-hidden">
											<Image
												src="/img/chainedor1.jpg"
												alt="Manoir La Chaine d'Or"
												fill
												className="object-cover transition-transform duration-500 group-hover:scale-110 p-1 rounded-2xl"
												sizes="(max-width: 768px) 100vw, 300px"
											/>
											<div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
										</div>
										<div className="p-5">
											<h4 className="text-lg font-bold text-bordeaux-800 mb-3 flex items-center gap-2 group-hover:text-bordeaux-900 transition-colors">
												<div className="p-1 bg-gold-100 rounded-full">
													<MapPin className="w-4 h-4 text-gold-600" />
												</div>
												Manoir La Chaine d'Or
											</h4>
											<div className="space-y-2 mb-4">
												<p className="text-sm text-bordeaux-600 flex items-center gap-2">
													<div className="p-1 bg-bordeaux-100 rounded-full">
														<Car className="w-3 h-3 text-bordeaux-500" />
													</div>
													<span className="font-medium">20 min du domaine</span>
												</p>
												<p className="text-xs text-bordeaux-500 leading-relaxed pl-7">
													25/27 Rue Grande, Le Petit Andelys, 27700 Les Andelys
												</p>
											</div>
										</div>
									</motion.div>
								</a>

								<a href="https://www.villa-alienor.fr/fr" target="_blank" rel="noopener">
									<motion.div
										initial={{ opacity: 0, y: 20 }}
										whileInView={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.7, delay: 0.4 }}
										viewport={{ once: true }}
										className="group bg-gradient-to-br from-white to-bordeaux-50/30 rounded-2xl shadow-xl border border-bordeaux-100/50 overflow-hidden hover:shadow-2xl hover:border-gold-300 transition-all duration-300 transform hover:-translate-y-1 h-full"
									>
										<div className="relative w-full h-40 overflow-hidden">
											<Image
												src="/img/alenore.jpg"
												alt="Villa Aliénor"
												fill
												className="object-cover transition-transform duration-500 group-hover:scale-110 p-1 rounded-2xl"
												sizes="(max-width: 768px) 100vw, 300px"
											/>
											<div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
										</div>
										<div className="p-5">
											<h4 className="text-lg font-bold text-bordeaux-800 mb-3 flex items-center gap-2 group-hover:text-bordeaux-900 transition-colors">
												<div className="p-1 bg-gold-100 rounded-full">
													<MapPin className="w-4 h-4 text-gold-600" />
												</div>
												Villa Aliénor
											</h4>
											<div className="space-y-2 mb-4">
												<p className="text-sm text-bordeaux-600 flex items-center gap-2">
													<div className="p-1 bg-bordeaux-100 rounded-full">
														<Car className="w-3 h-3 text-bordeaux-500" />
													</div>
													<span className="font-medium">20 min du domaine</span>
												</p>
												<p className="text-xs text-bordeaux-500 leading-relaxed pl-7">
													1 Rue Grande Entrée, Quai Grimoult, 27700 Les Andelys
												</p>
											</div>
										</div>
									</motion.div>
								</a>

								{/* Ligne 3 - Hôtels 5 & 6 */}
								<a href="https://lamaisoncarpediem.fr/" target="_blank" rel="noopener">
									<motion.div
										initial={{ opacity: 0, y: 20 }}
										whileInView={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.7, delay: 0.5 }}
										viewport={{ once: true }}
										className="group bg-gradient-to-br from-white to-bordeaux-50/30 rounded-2xl shadow-xl border border-bordeaux-100/50 overflow-hidden hover:shadow-2xl hover:border-gold-300 transition-all duration-300 transform hover:-translate-y-1 h-full"
									>
										<div className="relative w-full h-40 overflow-hidden">
											<Image
												src="/img/carpediem.png"
												alt="La maison carpe diem"
												fill
												className="object-cover transition-transform duration-500 group-hover:scale-110 p-1 rounded-2xl"
												sizes="(max-width: 768px) 100vw, 300px"
											/>
											<div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
										</div>
										<div className="p-5">
											<h4 className="text-lg font-bold text-bordeaux-800 mb-3 flex items-center gap-2 group-hover:text-bordeaux-900 transition-colors">
												<div className="p-1 bg-gold-100 rounded-full">
													<MapPin className="w-4 h-4 text-gold-600" />
												</div>
												La maison carpe diem
											</h4>
											<div className="space-y-2 mb-4">
												<p className="text-sm text-bordeaux-600 flex items-center gap-2">
													<div className="p-1 bg-bordeaux-100 rounded-full">
														<Car className="w-3 h-3 text-bordeaux-500" />
													</div>
													<span className="font-medium">20 min du domaine</span>
												</p>
												<p className="text-xs text-bordeaux-500 leading-relaxed pl-7">
													21 Rue Blanchard, 27700 Les Andelys
												</p>
											</div>
										</div>
									</motion.div>
								</a>

								<a href="https://www.booking.com/hotel/fr/club-wyndham-normandy.fr.html" target="_blank" rel="noopener">
									<motion.div
										initial={{ opacity: 0, y: 20 }}
										whileInView={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.7, delay: 0.6 }}
										viewport={{ once: true }}
										className="group bg-gradient-to-br from-white to-bordeaux-50/30 rounded-2xl shadow-xl border border-bordeaux-100/50 overflow-hidden hover:shadow-2xl hover:border-gold-300 transition-all duration-300 transform hover:-translate-y-1 h-full"
									>
										<div className="relative w-full h-40 overflow-hidden">
											<Image
												src="/img/club.jpg"
												alt="Club Wyndham Normandy"
												fill
												className="object-cover transition-transform duration-500 group-hover:scale-110 p-1 rounded-2xl"
												sizes="(max-width: 768px) 100vw, 300px"
											/>
											<div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
										</div>
										<div className="p-5">
											<h4 className="text-lg font-bold text-bordeaux-800 mb-3 flex items-center gap-2 group-hover:text-bordeaux-900 transition-colors">
												<div className="p-1 bg-gold-100 rounded-full">
													<MapPin className="w-4 h-4 text-gold-600" />
												</div>
												Club Wyndham Normandy
											</h4>
											<div className="space-y-2 mb-4">
												<p className="text-sm text-bordeaux-600 flex items-center gap-2">
													<div className="p-1 bg-bordeaux-100 rounded-full">
														<Car className="w-3 h-3 text-bordeaux-500" />
													</div>
													<span className="font-medium">20 min du domaine</span>
												</p>
												<p className="text-xs text-bordeaux-500 leading-relaxed pl-7">
													40B Route d'Amfreville-Sous-les-Monts, 27430 Connelles
												</p>
											</div>
										</div>
									</motion.div>
								</a>
							</div>
						</div>
					</div>
				</motion.div>
			</div>
		</section>
	)
}
